/**
 * The casing strings a job page renders, one card each (api#830 follow-up).
 *
 * `job_casing_strings` via the job read's `casingStrings` array is the source:
 * up to 20 strings, each with its own combo and therefore its own torque. The
 * office job page used to read the two flat column sets on `jobs` and so showed
 * strings 1 and 2 only — a third string was on the job and on the call sheet
 * and nowhere on the page.
 *
 * FALLBACK — only when the read carries NO `casingStrings` rows: the two legacy
 * flat sets, as that page always showed them. Every job was backfilled by 0289
 * and every write path dual-writes, so this should not fire; it exists so an
 * older payload renders what it used to rather than nothing.
 *
 * A string with none of its six spec columns set carries nothing to act on and
 * is dropped, as on the field screen (FieldJobDetail — see the note there on
 * Smith J-0017).
 */
import type { JobCasingString } from '../types/casing-string';

export const hasAnyCasingSpec = (s: JobCasingString): boolean =>
  !!(s.size_inches || s.size_mm || s.weight_lb_ft || s.weight_kg_m || s.grade || s.thread_type);

const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));

export function jobCasingStringsForDisplay(job: any): JobCasingString[] {
  const rows: JobCasingString[] = Array.isArray(job?.casingStrings) ? job.casingStrings : [];
  if (rows.length > 0) {
    return [...rows]
      .filter(hasAnyCasingSpec)
      .sort((a, b) => Number(a.string_number) - Number(b.string_number));
  }
  if (!job) return [];
  const legacy: JobCasingString[] = [
    {
      string_number: 1,
      size_inches: str(job.casingSizeInches ?? job.casing_size_inches),
      size_mm: str(job.casingSizeMm ?? job.casing_size_mm),
      weight_lb_ft: str(job.casingWeightLbFt ?? job.casing_weight_lb_ft),
      weight_kg_m: str(job.casingWeightKgM ?? job.casing_weight_kg_m),
      grade: str(job.casingGrade ?? job.casing_grade),
      thread_type: str(job.threadType ?? job.thread_type),
    },
    {
      string_number: 2,
      size_inches: str(job.casingSize2Inches ?? job.casing_size_2_inches),
      size_mm: str(job.casingSize2Mm ?? job.casing_size_2_mm),
      weight_lb_ft: str(job.casingWeight2LbFt ?? job.casing_weight_2_lb_ft),
      weight_kg_m: str(job.casingWeight2KgM ?? job.casing_weight_2_kg_m),
      grade: str(job.casingGrade2 ?? job.casing_grade_2),
      thread_type: str(job.threadType2 ?? job.thread_type_2),
    },
  ];
  return legacy.filter(hasAnyCasingSpec);
}

/**
 * The job save's 409 `CASING_STRING_HAS_METRES` (api#830): the edit removed a
 * casing string that a ticket holds actual metres on. The API's own sentence
 * names the strings and tickets; this rebuilds it from `holders` when a body
 * carries those but no sentence, and returns null for any other error so the
 * caller keeps its own handling.
 */
export function casingStringHasMetresMessage(err: any): string | null {
  const body = err?.response?.data;
  if (err?.response?.status !== 409 || body?.code !== 'CASING_STRING_HAS_METRES') return null;
  if (typeof body.error === 'string' && body.error) return body.error;
  const holders: any[] = Array.isArray(body.holders) ? body.holders : [];
  const byString = new Map<number, string[]>();
  for (const h of holders) {
    const label = `${h.ticketNumber ?? h.ticketId}${h.deleted ? ' (deleted)' : ''}`;
    byString.set(Number(h.stringNumber), [...(byString.get(Number(h.stringNumber)) ?? []), label]);
  }
  const parts = Array.from(byString.entries()).sort((a, b) => a[0] - b[0])
    .map(([n, t]) => `string ${n} (metres on ${t.join(', ')})`);
  return parts.length > 0
    ? `Cannot remove ${parts.join('; ')}. Clear the metres on those tickets first.`
    : 'A casing string you removed has metres recorded on a ticket. Clear the metres on that ticket first.';
}

/**
 * A string's name on a ticket's "Metres run" input: its number and the combo
 * that tells two strings apart — `String 2 — 7" 23 lb/ft L-80 LTC`. Absent
 * parts are left out rather than guessed; a string with none reads its number.
 */
export function casingStringLabel(s: JobCasingString): string {
  const parts = [
    s.size_inches ? `${s.size_inches}"` : null,
    s.weight_lb_ft ? `${s.weight_lb_ft} lb/ft` : null,
    s.grade || null,
    s.thread_type || null,
  ].filter(Boolean);
  return parts.length > 0 ? `String ${s.string_number} — ${parts.join(' ')}` : `String ${s.string_number}`;
}
